import React, { useRef, useMemo, useState } from 'react';
import { useDerivativeTasks, DerivativeResultActions, SlideDiffViewer } from '@starflow/app/modules/derivative';
import { DerivativeResults, DerivativeQueue, DerivativeTask } from '@starflow/app/modules/derivative';
import { useUnit, useProjectState } from '@starflow/app/store';
import { findMatchedResultIndex } from '@starflow/app/helpers/flow';
import { translate } from '@shakkerai/util-i18n';

const UpscaleOverlayer = React.memo(({ isMiniMode, element, viewport }) => {
  const unitId = element.lastDerivativeUnitId;
  const tasks = useDerivativeTasks(unitId);
  const currentNodeId = useProjectState('currentNodeId');
  const unit = useUnit(unitId);
  const matchedResultIndex = findMatchedResultIndex(unitId);
  const [selectedIndex, setSelectedIndex] = useState(unit.result?.selectedIndex ?? matchedResultIndex); // -1: 原图, >=0: 选择的任务索引
  const sourceElement = unit.data?.sourceElement || element;
  const selectedItem = tasks[selectedIndex];
  const resultsRef = useRef(null);

  const previewContent = useMemo(() => {
    if (!selectedItem) {
      return null;
    }

    if (selectedItem.type === 'queue') {
      return <DerivativeQueue data={selectedItem} />;
    }

    if (selectedItem.type === 'task') {
      return <DerivativeTask data={selectedItem} />;
    }

    if (selectedItem.url) {
      return (
        <SlideDiffViewer
          image={selectedItem}
          scale={viewport.scale}
          sourceElement={sourceElement}
          width={element.width * element.scaleX * viewport.scale}
          height={element.height * element.scaleY * viewport.scale}
          className="absolute inset-0 w-full h-full"
        />
      );
    }

    return <img className="absolute inset-0 w-full h-full" src={sourceElement.url} />;
  }, [sourceElement.url, viewport.scale, selectedItem]);

  const handleCancel = () => {
    resultsRef.current?.handleSelect(-1);
  };

  return (
    <>
      {!isMiniMode && (
        <DerivativeResults
          ref={resultsRef}
          items={tasks}
          elementId={element.id}
          flowId={element.flowId}
          selected={selectedIndex}
          onSelect={setSelectedIndex}
          sourceElement={unit.data?.sourceElement || element}
          currentNodeId={currentNodeId}
        />
      )}
      <div className="absolute -inset-0.5 border-2 border-primary">
        <div className="absolute -top-5 left-0 flex items-center h-4 text-primary whitespace-nowrap text-[10px]">
          <div className="w-2 h-2 border border-primary rotate-45 mr-1" />
          <span>
            {unit.label} - {translate('imagetool_upscale')}
          </span>
        </div>
        {previewContent}
      </div>
      <div className="absolute top-full left-1/2 translate-x-[-50%] w-[280px] flex items-center justify-center pt-2 whitespace-nowrap">
        {selectedIndex > -1 && selectedItem && <DerivativeResultActions data={selectedItem} onCancel={handleCancel} />}
      </div>
    </>
  );
});

export const wrapImageOverlayer = (Overlayer) =>
  React.memo((props) => {
    const { currentElementTool, isEditing, isMiniMode, element, viewport } = props;
    const isActive = currentElementTool === 'upscale' && isEditing;

    if (isActive && element.lastDerivativeUnitId) {
      return <UpscaleOverlayer isMiniMode={isMiniMode} element={element} viewport={viewport} />;
    }

    return <Overlayer {...props} />;
  });
